import TextEditorCustomize from '@/components/TextEditorCustomize'
import {
    DatacentralHookForm,
    type DatacentralHookFormDataItem,
} from '@/components/ui/form-field'
import { AsyncFilterSelect } from '@/components/ui/filters/async-filter-select'
import { DEPARTMENT_IDS } from '@/constants/departments'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import useUploadFile from '@/hooks/useUploadFile'
import {
    getEmployeesDefaultOptions,
    getEmployeesOptionsPaginated,
} from '@/modules/Employee/services'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import { createContractProcess } from '@/modules/Contract/modules/Process/services'
import { ContractProcessCreateParams } from '@/modules/Contract/modules/Process/types'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import { formatDateWithFormatter, renderFileItem } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, FormItem } from 'vnetwork-ui'
import { schemaCreateContractProcessModal } from './schema'

export interface CreateContractProcessFormValues {
    contract: ContractProcessCreateParams
    attachments?: File[]
}

export interface CreateContractProcessModalProps extends ModalProps {
    contractID: number
}

const CreateContractProcessModal = ({
    onClose,
    open,
    contractID,
}: CreateContractProcessModalProps) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        mode: 'contains',
        pattern: 'processes',
    })

    const formControl = useForm<CreateContractProcessFormValues>({
        defaultValues: {
            contract: {
                contract_process_title: '',
                contract_process_content: '',
                contract_process_problem: '',
                contract_process_assignees:
                    import.meta.env.APP_PROCESS_ASSIGNEE_IDS?.split(',')?.map(
                        Number
                    ) || [],
                contract_process_deadline: dayjs(),
                contract_process_priority: 'medium',
            },
            attachments: [],
        },
        resolver: yupResolver(schemaCreateContractProcessModal as any),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: createContractProcess,
        },
        {
            successMessage: t('add-new-process-success'),
            reactQueryKeys: QUERY_KEYS.PROCESSES,
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const { isUploading, uploadAsync } = useUploadFile({
        prefix: STORGE_PREFIXS.CONTRACT_PROCESS,
    })

    const handleCreate = async (values: CreateContractProcessFormValues) => {
        let attachments: Attachment[] = []
        if (values?.attachments) {
            const attachmentUploadeds = await uploadAsync(values?.attachments)

            attachments = attachmentUploadeds
                ?.filter((attachment) => attachment.status === 'fulfilled')
                .map((attachment) => attachment.value) as Attachment[]
        }

        mutateAsync({
            contractID,
            data: {
                ...values.contract,
                contract_process_deadline: formatDateWithFormatter(
                    values?.contract?.contract_process_deadline,
                    'YYYY-MM-DDTHH:mm:ssZ'
                ),
            },
            attachments,
        })
    }

    const isLoading = isUploading || isPending

    const formData: DatacentralHookFormDataItem<CreateContractProcessFormValues>[] =
        [
            {
                formItemProps: {
                    name: 'contract.contract_process_title',
                    type: 'textfield',
                    label: t('contract_process_title'),
                    placeholder: t('contract_process_title'),
                },
                colSpan: 4,
            },
            {
                renderEditComponent() {
                    return (
                        <TextEditorCustomize
                            label={t('contract_process_content')}
                            value={{
                                text: formControl.getValues(
                                    'contract.contract_process_content'
                                ),
                                attachments: [],
                            }}
                            placeholder={t('contract_process_content')}
                            onChange={(value) => {
                                formControl.setValue(
                                    'contract.contract_process_content',
                                    value.text
                                )
                            }}
                        />
                    )
                },
                colSpan: 4,
            },
            {
                formItemProps: {
                    name: 'contract.contract_process_problem',
                    type: 'textarea',
                    label: t('contract_process_problem'),
                    placeholder: t('contract_process_problem'),
                },
                colSpan: 4,
            },
            {
                renderEditComponent(control) {
                    return (
                        <AsyncFilterSelect
                            control={control}
                            name={'contract.contract_process_assignees' as any}
                            label={t('contract_process_assignees')}
                            multiple
                            asyncOptions={(query, page) =>
                                getEmployeesOptionsPaginated(
                                    query,
                                    [
                                        DEPARTMENT_IDS.FINANCE,
                                        DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
                                    ],
                                    'active',
                                    page
                                )
                            }
                            getDefaultOptions={getEmployeesDefaultOptions}
                        />
                    )
                },
                colSpan: 4,
            },
            {
                formItemProps: {
                    name: 'contract.contract_process_deadline',
                    type: 'datepicker',
                    label: t('contract_process_deadline'),
                    placeholder: t('contract_process_deadline'),
                },
                colSpan: 4,
            },
            {
                renderEditComponent(control) {
                    return (
                        <FormItem
                            control={control}
                            name="attachments"
                            type="files"
                            renderFileItem={renderFileItem}
                            label={t('attachments')}
                            multiple={true}
                        />
                    )
                },
                colSpan: 4,
            },
        ]

    return (
        <Dialog
            title={t('create-contract-process')}
            onClose={onClose}
            open={open}
        >
            <DatacentralHookForm
                formControl={formControl}
                className="mt-2"
                onSubmit={handleCreate}
                data={formData}
                actions={[
                    {
                        title: t('cancel-operation'),
                        type: 'button',
                        variant: 'outline',
                        handler: onClose,
                        disabled: isLoading,
                    },
                    {
                        title: t('create'),
                        type: 'submit',
                        variant: 'default',
                        loading: isLoading,
                        disabled: isLoading,
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateContractProcessModal
